import React, { useEffect, useState, useRef } from 'react';
import styled, { keyframes } from 'styled-components';
import { Container } from '../../../components/commons';
import useToast from '../../../hooks/useToast';
import { aiService, AiConfigData } from '../../../services/ai.service';
import { api } from '../../../services/api';
import {
  FaRobot,
  FaPlus,
  FaPlug,
  FaTrash,
  FaCheck,
  FaRotate,
  FaWandMagicSparkles,
  FaFileLines,
  FaComments,
  FaPaperPlane,
  FaUser,
  FaLightbulb,
  FaBroom,
} from 'react-icons/fa6';
import Modal from '../../../components/Modals/Modal';

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 24px;
  flex-wrap: wrap;
  gap: 16px;
`;

const Title = styled.h1`
  font-size: 24px;
  font-weight: 700;
  color: #1e293b;
  display: flex;
  align-items: center;
  gap: 12px;
`;

const Badge = styled.span<{ variant?: 'success' | 'info' | 'warning' }>`
  background-color: ${({ variant }) =>
    variant === 'success' ? '#dcfce7' : variant === 'info' ? '#e0f2fe' : '#fef9c3'};
  color: ${({ variant }) =>
    variant === 'success' ? '#15803d' : variant === 'info' ? '#0369a1' : '#a16207'};
  padding: 4px 10px;
  border-radius: 9999px;
  font-size: 12px;
  font-weight: 600;
`;

const Card = styled.div`
  background: #ffffff;
  border-radius: 12px;
  padding: 24px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  margin-bottom: 24px;
`;

const Button = styled.button<{ variant?: 'primary' | 'secondary' | 'danger' }>`
  background-color: ${({ variant }) =>
    variant === 'secondary' ? '#f1f5f9' : variant === 'danger' ? '#ef4444' : '#059669'};
  color: ${({ variant }) => (variant === 'secondary' ? '#334155' : '#ffffff')};
  border: none;
  padding: 10px 18px;
  border-radius: 8px;
  font-weight: 600;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  transition: all 0.2s;

  &:hover {
    opacity: 0.9;
    transform: translateY(-1px);
  }
`;

const FormGroup = styled.div`
  margin-bottom: 16px;
  display: flex;
  flex-direction: column;
  gap: 6px;

  label {
    font-size: 14px;
    font-weight: 600;
    color: #475569;
  }

  input, select, textarea {
    padding: 10px 14px;
    border: 1px solid #cbd5e1;
    border-radius: 8px;
    font-size: 14px;
    outline: none;
    transition: border-color 0.2s;

    &:focus {
      border-color: #059669;
    }
  }
`;

const SwitchRow = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  margin-top: 8px;

  label {
    font-size: 14px;
    color: #334155;
    cursor: pointer;
  }
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  text-align: left;

  th {
    padding: 12px 16px;
    background-color: #f8fafc;
    color: #64748b;
    font-size: 12px;
    text-transform: uppercase;
    font-weight: 600;
    border-bottom: 1px solid #e2e8f0;
  }

  td {
    padding: 14px 16px;
    border-bottom: 1px solid #f1f5f9;
    color: #334155;
    font-size: 14px;
  }
`;

const ResultBox = styled.pre`
  background: #0f172a;
  color: #38bdf8;
  padding: 16px;
  border-radius: 8px;
  overflow-x: auto;
  font-size: 13px;
  max-height: 300px;
`;

const pulseAnim = keyframes`
  0% { transform: scale(0.95); opacity: 0.8; }
  50% { transform: scale(1.15); opacity: 1; }
  100% { transform: scale(0.95); opacity: 0.8; }
`;

const ChatContainer = styled.div`
  margin-top: 24px;
  background: #ffffff;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.04);
  display: flex;
  flex-direction: column;
  overflow: hidden;
`;

const ChatHeader = styled.div`
  background: #f8fafc;
  padding: 14px 20px;
  border-bottom: 1px solid #e2e8f0;
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 12px;
`;

const StatusIndicator = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 13px;
  font-weight: 600;
  color: #0f172a;
`;

const StatusDot = styled.span`
  width: 9px;
  height: 9px;
  border-radius: 50%;
  background-color: #10b981;
  display: inline-block;
  animation: ${pulseAnim} 2s infinite ease-in-out;
  box-shadow: 0 0 0 3px rgba(16, 185, 129, 0.2);
`;

const ChatMessagesArea = styled.div`
  padding: 20px;
  height: 380px;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  gap: 16px;
  background: #fcfcfd;
`;

const MessageBubble = styled.div<{ isUser: boolean }>`
  display: flex;
  align-self: ${({ isUser }) => (isUser ? 'flex-end' : 'flex-start')};
  max-width: 85%;
  gap: 12px;
  flex-direction: ${({ isUser }) => (isUser ? 'row-reverse' : 'row')};
`;

const AvatarBox = styled.div<{ isUser: boolean }>`
  width: 34px;
  height: 34px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 14px;
  flex-shrink: 0;
  background: ${({ isUser }) => (isUser ? '#1e293b' : '#059669')};
  color: #ffffff;
  box-shadow: 0 2px 5px rgba(0, 0, 0, 0.1);
`;

const BubbleContent = styled.div<{ isUser: boolean }>`
  background: ${({ isUser }) => (isUser ? '#1e293b' : '#ffffff')};
  color: ${({ isUser }) => (isUser ? '#ffffff' : '#1e293b')};
  border: 1px solid ${({ isUser }) => (isUser ? '#1e293b' : '#e2e8f0')};
  border-radius: 12px;
  padding: 12px 16px;
  font-size: 14px;
  line-height: 1.6;
  white-space: pre-wrap;
  word-break: break-word;
  box-shadow: ${({ isUser }) => (isUser ? '0 2px 6px rgba(0,0,0,0.1)' : '0 1px 3px rgba(0,0,0,0.04)')};

  strong {
    color: ${({ isUser }) => (isUser ? '#38bdf8' : '#047857')};
    font-weight: 700;
  }
`;

const TimeStamp = styled.div<{ isUser: boolean }>`
  font-size: 11px;
  color: #94a3b8;
  margin-top: 4px;
  text-align: ${({ isUser }) => (isUser ? 'right' : 'left')};
`;

const QuickChipsRow = styled.div`
  display: flex;
  gap: 8px;
  padding: 10px 20px;
  background: #f8fafc;
  border-top: 1px solid #f1f5f9;
  overflow-x: auto;
  align-items: center;

  span {
    font-size: 12px;
    font-weight: 600;
    color: #64748b;
    white-space: nowrap;
    display: flex;
    align-items: center;
    gap: 4px;
  }
`;

const Chip = styled.button`
  background: #ffffff;
  border: 1px solid #cbd5e1;
  border-radius: 20px;
  padding: 6px 12px;
  font-size: 12px;
  font-weight: 500;
  color: #475569;
  cursor: pointer;
  white-space: nowrap;
  transition: all 0.2s;

  &:hover {
    background: #059669;
    color: #ffffff;
    border-color: #059669;
    transform: translateY(-1px);
  }
`;

const ChatInputArea = styled.div`
  display: flex;
  padding: 14px 20px;
  gap: 12px;
  background: #ffffff;
  border-top: 1px solid #e2e8f0;
  align-items: center;

  input {
    flex: 1;
    padding: 12px 16px;
    border: 1px solid #cbd5e1;
    border-radius: 8px;
    font-size: 14px;
    outline: none;
    transition: border-color 0.2s;

    &:focus {
      border-color: #059669;
    }

    &:disabled {
      background: #f8fafc;
      cursor: not-allowed;
    }
  }
`;

const AiSettingsPage: React.FC = () => {
  const toast = useToast();
  const [configs, setConfigs] = useState<AiConfigData[]>([]);
  const [groups, setGroups] = useState<any[]>([]);
  const [documents, setDocuments] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadingDocs, setLoadingDocs] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [testingConnection, setTestingConnection] = useState(false);
  const [connectionStatus, setConnectionStatus] = useState<string | null>(null);

  // Form State
  const [formData, setFormData] = useState<Partial<AiConfigData>>({
    name: '',
    provider: 'gemini',
    modelName: 'gemini-2.5-flash',
    apiKey: '',
    temperature: 0.1,
    autoClassification: true,
    autoExtraction: true,
    isDefault: false,
  });

  // POC Demo State
  const [selectedDocId, setSelectedDocId] = useState('');
  const [demoLoading, setDemoLoading] = useState(false);
  const [demoResult, setDemoResult] = useState<any>(null);

  const fetchConfigs = async () => {
    try {
      setLoading(true);
      const res = await aiService.getConfigs();
      setConfigs(res.data || []);
    } catch (err: any) {
      toast.errorToast('Erro ao carregar configurações de IA.');
    } finally {
      setLoading(false);
    }
  };

  const fetchGroupsAndDocs = async () => {
    try {
      setLoadingDocs(true);
      const [groupsRes, docsRes] = await Promise.all([
        api.get('/groups'),
        api.get('/documents/global-search?perPage=100&isFolder=false'),
      ]);
      const groupList = Array.isArray(groupsRes.data?.data)
        ? groupsRes.data.data
        : Array.isArray(groupsRes.data)
        ? groupsRes.data
        : [];
      setGroups(groupList);

      const docList = Array.isArray(docsRes.data?.data)
        ? docsRes.data.data
        : Array.isArray(docsRes.data)
        ? docsRes.data
        : [];
      setDocuments(docList);
    } catch (e) {
      console.error('Erro ao buscar documentos para POC:', e);
    } finally {
      setLoadingDocs(false);
    }
  };

  useEffect(() => {
    fetchConfigs();
    fetchGroupsAndDocs();
  }, []);

  const handleOpenModal = (configToEdit?: AiConfigData) => {
    if (configToEdit) {
      setFormData(configToEdit);
    } else {
      setFormData({
        name: '',
        provider: 'gemini',
        modelName: 'gemini-2.5-flash',
        apiKey: '',
        temperature: 0.1,
        autoClassification: true,
        autoExtraction: true,
        isDefault: configs.length === 0,
      });
    }
    setConnectionStatus(null);
    setIsModalOpen(true);
  };

  const handleTestConnection = async () => {
    if (!formData.apiKey) {
      toast.errorToast('Informe a chave de API para testar.');
      return;
    }
    setTestingConnection(true);
    setConnectionStatus(null);
    try {
      const res = await aiService.testConnection({
        apiKey: formData.apiKey,
        modelName: formData.modelName || 'gemini-2.5-flash',
        provider: formData.provider,
      });
      if (res.data?.success) {
        setConnectionStatus('✅ Conectado com sucesso ao Google Gemini!');
        toast.successToast('Conexão com Gemini 2.5 Flash bem-sucedida!');
      } else {
        setConnectionStatus(`❌ Falha: ${res.data?.message}`);
        toast.errorToast(res.data?.message || 'Falha na conexão');
      }
    } catch (err: any) {
      setConnectionStatus(`❌ Erro: ${err.message}`);
      toast.errorToast('Erro ao testar conexão com IA.');
    } finally {
      setTestingConnection(false);
    }
  };

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.name || !formData.apiKey) {
      toast.errorToast('Preencha os campos obrigatórios (Nome e Chave de API).');
      return;
    }

    try {
      await aiService.saveConfig(formData, formData._id);
      toast.successToast('Configuração de IA salva com sucesso!');
      setIsModalOpen(false);
      fetchConfigs();
    } catch (err: any) {
      toast.errorToast('Erro ao salvar configuração.');
    }
  };

  const handleDelete = async (id: string) => {
    if (window.confirm('Tem certeza que deseja excluir esta configuração?')) {
      try {
        await aiService.deleteConfig(id);
        toast.successToast('Configuração excluída.');
        fetchConfigs();
      } catch (err) {
        toast.errorToast('Erro ao excluir.');
      }
    }
  };

  // Demonstração / Teste ao vivo da POC
  const handleRunPocDemo = async () => {
    if (!selectedDocId) {
      toast.errorToast('Selecione um documento para processar com a IA.');
      return;
    }

    setDemoLoading(true);
    setDemoResult(null);

    try {
      const res = await aiService.process(selectedDocId);
      setDemoResult(res.data);
      toast.successToast('Documento processado com sucesso pelo Gemini 2.5 Flash!');
    } catch (err: any) {
      toast.errorToast(err?.response?.data?.message || 'Erro no processamento de IA.');
    } finally {
      setDemoLoading(false);
    }
  };

  // Chat Interativo com o Documento
  const [chatMessages, setChatMessages] = useState<Array<{ role: 'user' | 'model'; text: string; time?: string }>>([]);
  const [chatInput, setChatInput] = useState('');
  const [chatLoading, setChatLoading] = useState(false);
  const chatBottomRef = useRef<HTMLDivElement>(null);

  const selectedDocument = documents.find((d) => d._id === selectedDocId);

  useEffect(() => {
    if (selectedDocId) {
      const doc = documents.find((d) => d._id === selectedDocId);
      setChatMessages([
        {
          role: 'model',
          text: `Olá! Estou pronto para analisar o documento **"${doc?.name || 'selecionado'}"** utilizando o **Gemini 2.5 Flash**.\n\nVocê pode me fazer perguntas pontuais sobre nomes, números de processos, valores, datas de vencimento ou pedir sínteses arquivísticas. Como posso ajudar?`,
          time: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
        },
      ]);
    } else {
      setChatMessages([]);
    }
  }, [selectedDocId]);

  useEffect(() => {
    chatBottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [chatMessages, chatLoading]);

  const handleSendMessage = async (textToSend?: string) => {
    const text = (textToSend || chatInput).trim();
    if (!text || chatLoading) return;
    if (!selectedDocId) {
      toast.errorToast('Selecione um documento antes de iniciar o chat.');
      return;
    }

    const timeStr = new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    const userMsg = { role: 'user' as const, text, time: timeStr };

    // Formatar histórico para o Gemini (excluindo a mensagem inicial de boas-vindas)
    const historyPayload = chatMessages
      .filter((_, idx) => idx > 0)
      .map((m) => ({ role: m.role, text: m.text }));

    setChatMessages((prev) => [...prev, userMsg]);
    setChatInput('');
    setChatLoading(true);

    try {
      const res = await aiService.chat(selectedDocId, text, historyPayload);
      const answer = res.data?.answer || res.answer || 'Não obtive resposta do modelo.';
      setChatMessages((prev) => [
        ...prev,
        {
          role: 'model',
          text: answer,
          time: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
        },
      ]);
    } catch (err: any) {
      toast.errorToast(err?.response?.data?.message || 'Erro ao comunicar com o chat da IA.');
      setChatMessages((prev) => [
        ...prev,
        {
          role: 'model',
          text: '❌ Desculpe, ocorreu um erro ao consultar o Gemini para este documento.',
          time: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
        },
      ]);
    } finally {
      setChatLoading(false);
    }
  };

  const handleClearChat = () => {
    if (selectedDocId) {
      const doc = documents.find((d) => d._id === selectedDocId);
      setChatMessages([
        {
          role: 'model',
          text: `Conversa reiniciada para o documento **"${doc?.name || 'selecionado'}"**. Em que posso auxiliar?`,
          time: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
        },
      ]);
    } else {
      setChatMessages([]);
    }
  };

  const quickPrompts = [
    '📌 Resuma os pontos principais deste documento',
    '🔍 Quais dados cadastrais e identificadores constam aqui?',
    '💰 Identifique valores monetários, parcelas ou empenhos citados',
    '⚠️ Há alguma pendência, rasura ou inconformidade aparente?',
    '🏛️ Qual a finalidade arquivística deste documento?',
  ];

  return (
    <Container>
      <Header>
        <div>
          <Title>
            <FaRobot color="#059669" />
            Módulo de Inteligência Artificial — Parametrização & POC
          </Title>
          <p style={{ color: '#64748b', marginTop: 4 }}>
            Configuração de provedores de IA por unidade administrativa (Google Gemini 2.5 Flash). Atendimento ao Item 4.2.22.4 do TR.
          </p>
        </div>
        <Button onClick={() => handleOpenModal()}>
          <FaPlus /> Nova Configuração de IA
        </Button>
      </Header>

      {/* Cartão de Provedores Cadastrados */}
      <Card>
        <h2 style={{ fontSize: 18, marginBottom: 16, color: '#1e293b' }}>
          Mecanismos de IA por Unidade Administrativa
        </h2>
        {configs.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '30px', color: '#94a3b8' }}>
            Nenhuma configuração de IA cadastrada. Clique no botão acima para adicionar sua chave da API do Google Gemini.
          </div>
        ) : (
          <Table>
            <thead>
              <tr>
                <th>Nome / Descrição</th>
                <th>Unidade</th>
                <th>Provedor</th>
                <th>Modelo</th>
                <th>Classificação</th>
                <th>Indexação</th>
                <th>Padrão</th>
                <th>Ações</th>
              </tr>
            </thead>
            <tbody>
              {configs.map((c) => (
                <tr key={c._id}>
                  <td><strong>{c.name}</strong></td>
                  <td>{c.unitId?.name || <Badge variant="info">Global / Todas</Badge>}</td>
                  <td>
                    <Badge variant="success">Google Gemini</Badge>
                  </td>
                  <td><code>{c.modelName}</code></td>
                  <td>{c.autoClassification ? '✅ Ativo' : '❌ Inativo'}</td>
                  <td>{c.autoExtraction ? '✅ Ativo' : '❌ Inativo'}</td>
                  <td>{c.isDefault ? <Badge variant="success">Padrão</Badge> : '—'}</td>
                  <td>
                    <div style={{ display: 'flex', gap: 8 }}>
                      <Button variant="secondary" style={{ padding: '6px 12px' }} onClick={() => handleOpenModal(c)}>
                        Editar
                      </Button>
                      <Button variant="danger" style={{ padding: '6px 12px' }} onClick={() => c._id && handleDelete(c._id)}>
                        <FaTrash />
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        )}
      </Card>

      {/* Cartão de Teste ao Vivo / Prova de Conceito (POC) */}
      <Card style={{ borderLeft: '4px solid #059669' }}>
        <h2 style={{ fontSize: 18, marginBottom: 8, color: '#1e293b', display: 'flex', alignItems: 'center', gap: 8 }}>
          <FaWandMagicSparkles color="#059669" /> Demonstração Operacional para Prova de Conceito (POC)
        </h2>
        <p style={{ color: '#64748b', fontSize: 14, marginBottom: 16 }}>
          Execute ao vivo a classificação automática e a extração estruturada de metadados utilizando o <strong>Gemini 2.5 Flash</strong> sobre documentos reais do repositório.
        </p>

        <div style={{ display: 'flex', gap: 12, alignItems: 'flex-end', flexWrap: 'wrap' }}>
          <FormGroup style={{ flex: 1, minWidth: 260, marginBottom: 0 }}>
            <label style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span>Selecione um Documento do GED:</span>
              <span
                style={{ fontSize: 12, color: '#059669', cursor: 'pointer', fontWeight: 600, userSelect: 'none' }}
                onClick={() => fetchGroupsAndDocs()}
                title="Clique para recarregar lista de documentos"
              >
                🔄 Atualizar ({documents.length} disponíveis)
              </span>
            </label>
            <select
              value={selectedDocId}
              onChange={(e) => setSelectedDocId(e.target.value)}
              disabled={loadingDocs || demoLoading}
            >
              <option value="">
                {loadingDocs
                  ? 'Carregando documentos do GED...'
                  : documents.length === 0
                  ? 'Nenhum documento encontrado no repositório'
                  : 'Selecione um arquivo...'}
              </option>
              {documents.map((d) => (
                <option key={d._id} value={d._id}>
                  📄 {d.name} {d.pages ? `(${d.pages} pág)` : ''} {d.documentTypeWithData?.documentType?.name ? `— [${d.documentTypeWithData.documentType.name}]` : ''}
                </option>
              ))}
            </select>
          </FormGroup>

          <Button onClick={handleRunPocDemo} disabled={demoLoading || !selectedDocId}>
            {demoLoading ? <FaRotate className="spin" /> : <FaWandMagicSparkles />}
            {demoLoading ? 'Processando com Gemini...' : 'Executar Demonstração com IA'}
          </Button>
        </div>

        {demoResult && (
          <div style={{ marginTop: 24 }}>
            <h3 style={{ fontSize: 15, fontWeight: 700, color: '#0f172a', marginBottom: 8 }}>
              Resultado Retornado pelo Gemini 2.5 Flash:
            </h3>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: 16, marginBottom: 16 }}>
              <div style={{ background: '#f8fafc', padding: 14, borderRadius: 8, border: '1px solid #e2e8f0' }}>
                <div style={{ fontSize: 12, color: '#64748b', fontWeight: 600 }}>TIPOLOGIA CLASSIFICADA</div>
                <div style={{ fontSize: 16, fontWeight: 700, color: '#059669', marginTop: 4 }}>
                  {demoResult.classification?.selectedTypeName}
                </div>
                <div style={{ fontSize: 12, color: '#475569', marginTop: 4 }}>
                  Confiança: <strong>{Math.round((demoResult.classification?.confidence || 0) * 100)}%</strong>
                </div>
                {demoResult.classification?.justification && (
                  <div style={{ fontSize: 12, color: '#64748b', marginTop: 6, fontStyle: 'italic' }}>
                    "{demoResult.classification.justification}"
                  </div>
                )}
              </div>

              <div style={{ background: '#f8fafc', padding: 14, borderRadius: 8, border: '1px solid #e2e8f0' }}>
                <div style={{ fontSize: 12, color: '#64748b', fontWeight: 600 }}>RESUMO CONCISO GERADO</div>
                <div style={{ fontSize: 13, color: '#334155', marginTop: 4 }}>
                  {demoResult.extraction?.summary || 'Resumo automático processado.'}
                </div>
              </div>
            </div>

            {demoResult.extraction?.extractedFields && demoResult.extraction.extractedFields.length > 0 && (
              <div style={{ marginBottom: 16, background: '#f8fafc', padding: 16, borderRadius: 8, border: '1px solid #e2e8f0' }}>
                <div style={{ fontSize: 12, color: '#64748b', fontWeight: 700, marginBottom: 10, textTransform: 'uppercase' }}>
                  Metadados Estruturados Extraídos:
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 12 }}>
                  {demoResult.extraction.extractedFields.map((field: any, idx: number) => (
                    <div key={idx} style={{ background: '#ffffff', padding: '10px 14px', borderRadius: 6, border: '1px solid #cbd5e1' }}>
                      <span style={{ fontSize: 11, color: '#64748b', fontWeight: 600, display: 'block' }}>{field.name}</span>
                      <strong style={{ fontSize: 13, color: '#1e293b' }}>{field.value || '— (não identificado)'}</strong>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <h4 style={{ fontSize: 13, fontWeight: 600, color: '#64748b', marginBottom: 6 }}>JSON ESTRUTURADO DE RESPOSTA:</h4>
            <ResultBox>{JSON.stringify(demoResult, null, 2)}</ResultBox>
          </div>
        )}

        {/* Chat Interativo com o Documento */}
        <ChatContainer>
          <ChatHeader>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <div style={{ background: '#059669', color: '#fff', padding: 8, borderRadius: 8, display: 'flex' }}>
                <FaComments size={18} />
              </div>
              <div>
                <StatusIndicator>
                  <StatusDot />
                  Chat Interativo com o Documento (Gemini 2.5 Flash)
                </StatusIndicator>
                <div style={{ fontSize: 12, color: '#64748b', marginTop: 2 }}>
                  {selectedDocument
                    ? `Conectado ao documento: ${selectedDocument.name}`
                    : 'Selecione um documento acima para habilitar o chat'}
                </div>
              </div>
            </div>

            {selectedDocId && (
              <Button
                variant="secondary"
                style={{ padding: '6px 12px', fontSize: 12 }}
                onClick={handleClearChat}
                title="Reiniciar chat"
              >
                <FaBroom /> Limpar Chat
              </Button>
            )}
          </ChatHeader>

          {/* Sugestões de Perguntas Rápidas */}
          {selectedDocId && (
            <QuickChipsRow>
              <span>
                <FaLightbulb color="#eab308" /> Sugestões:
              </span>
              {quickPrompts.map((prompt, idx) => (
                <Chip
                  key={idx}
                  onClick={() => handleSendMessage(prompt.replace(/^[^\s]+\s/, ''))}
                  disabled={chatLoading}
                >
                  {prompt}
                </Chip>
              ))}
            </QuickChipsRow>
          )}

          <ChatMessagesArea>
            {!selectedDocId ? (
              <div style={{ textAlign: 'center', margin: 'auto', color: '#94a3b8', padding: 20 }}>
                <FaComments size={40} style={{ marginBottom: 12, opacity: 0.5 }} />
                <p style={{ fontSize: 14, fontWeight: 500 }}>
                  Selecione um documento no campo acima para iniciar a conversa interativa com a IA.
                </p>
                <p style={{ fontSize: 12 }}>
                  O Gemini 2.5 Flash analisará o texto OCR e responderá qualquer dúvida específica.
                </p>
              </div>
            ) : chatMessages.length === 0 ? (
              <div style={{ textAlign: 'center', margin: 'auto', color: '#94a3b8' }}>
                Inicie uma conversa digitando abaixo ou clicando em uma das sugestões acima.
              </div>
            ) : (
              chatMessages.map((msg, idx) => {
                const isUser = msg.role === 'user';
                return (
                  <MessageBubble key={idx} isUser={isUser}>
                    <AvatarBox isUser={isUser}>
                      {isUser ? <FaUser size={14} /> : <FaRobot size={15} />}
                    </AvatarBox>
                    <div style={{ maxWidth: '100%' }}>
                      <BubbleContent isUser={isUser}>{msg.text}</BubbleContent>
                      {msg.time && <TimeStamp isUser={isUser}>{msg.time}</TimeStamp>}
                    </div>
                  </MessageBubble>
                );
              })
            )}

            {chatLoading && (
              <MessageBubble isUser={false}>
                <AvatarBox isUser={false}>
                  <FaRobot size={15} />
                </AvatarBox>
                <div>
                  <BubbleContent
                    isUser={false}
                    style={{
                      color: '#059669',
                      fontStyle: 'italic',
                      display: 'flex',
                      alignItems: 'center',
                      gap: 8,
                    }}
                  >
                    <FaRotate className="spin" /> Gemini 2.5 Flash está analisando o documento...
                  </BubbleContent>
                </div>
              </MessageBubble>
            )}

            <div ref={chatBottomRef} />
          </ChatMessagesArea>

          <ChatInputArea>
            <input
              type="text"
              placeholder={
                selectedDocId
                  ? 'Faça uma pergunta sobre este documento (pressione Enter para enviar)...'
                  : 'Selecione um documento acima para habilitar o chat...'
              }
              value={chatInput}
              onChange={(e) => setChatInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  handleSendMessage();
                }
              }}
              disabled={!selectedDocId || chatLoading}
            />
            <Button
              onClick={() => handleSendMessage()}
              disabled={!selectedDocId || chatLoading || !chatInput.trim()}
              style={{ minWidth: 110 }}
            >
              {chatLoading ? <FaRotate className="spin" /> : <FaPaperPlane />}
              {chatLoading ? 'Enviando...' : 'Enviar'}
            </Button>
          </ChatInputArea>
        </ChatContainer>
      </Card>

      {/* Modal de Criação / Edição de Configuração */}
      {isModalOpen && (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(0,0,0,0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 9999,
            padding: 16,
          }}
        >
          <div style={{ background: '#fff', borderRadius: 12, padding: 24, maxWidth: 540, width: '100%', maxHeight: '90vh', overflowY: 'auto' }}>
            <h2 style={{ fontSize: 18, fontWeight: 700, marginBottom: 16, color: '#1e293b' }}>
              {formData._id ? 'Editar Configuração de IA' : 'Cadastrar Mecanismo de IA'}
            </h2>

            <form onSubmit={handleSave}>
              <FormGroup>
                <label>Identificador da Configuração *</label>
                <input
                  type="text"
                  placeholder="Ex: Gabinete / Câmara Municipal ou Secretaria"
                  value={formData.name || ''}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  required
                />
              </FormGroup>

              <FormGroup>
                <label>Vincular à Unidade Administrativa (Grupo)</label>
                <select
                  value={formData.unitId || ''}
                  onChange={(e) => setFormData({ ...formData, unitId: e.target.value || undefined })}
                >
                  <option value="">Global / Todas as Unidades</option>
                  {groups.map((g) => (
                    <option key={g._id} value={g._id}>
                      {g.name}
                    </option>
                  ))}
                </select>
              </FormGroup>

              <FormGroup>
                <label>Provedor de IA (Agnóstico)</label>
                <select
                  value={formData.provider || 'gemini'}
                  onChange={(e) => setFormData({ ...formData, provider: e.target.value as any })}
                >
                  <option value="gemini">Google Gemini (Oficial)</option>
                  <option value="openai">OpenAI GPT-4o (Compatível)</option>
                  <option value="claude">Anthropic Claude (Compatível)</option>
                  <option value="ollama">Modelo Local / On-Premise (LGPD)</option>
                </select>
              </FormGroup>

              <FormGroup>
                <label>Modelo de Inteligência Artificial *</label>
                <input
                  type="text"
                  value={formData.modelName || 'gemini-2.5-flash'}
                  onChange={(e) => setFormData({ ...formData, modelName: e.target.value })}
                  placeholder="gemini-2.5-flash"
                  required
                />
              </FormGroup>

              <FormGroup>
                <label>Chave de API (API Key) *</label>
                <div style={{ display: 'flex', gap: 8 }}>
                  <input
                    type="password"
                    style={{ flex: 1 }}
                    placeholder="AIzaSy..."
                    value={formData.apiKey || ''}
                    onChange={(e) => setFormData({ ...formData, apiKey: e.target.value })}
                    required
                  />
                  <Button type="button" variant="secondary" onClick={handleTestConnection} disabled={testingConnection}>
                    <FaPlug /> {testingConnection ? 'Testando...' : 'Testar'}
                  </Button>
                </div>
                {connectionStatus && (
                  <div style={{ fontSize: 13, marginTop: 4, fontWeight: 600 }}>{connectionStatus}</div>
                )}
              </FormGroup>

              <div style={{ margin: '16px 0', padding: '12px', background: '#f8fafc', borderRadius: 8 }}>
                <SwitchRow>
                  <input
                    type="checkbox"
                    id="autoClassification"
                    checked={formData.autoClassification ?? true}
                    onChange={(e) => setFormData({ ...formData, autoClassification: e.target.checked })}
                  />
                  <label htmlFor="autoClassification">Habilitar Classificação Automática com IA</label>
                </SwitchRow>

                <SwitchRow>
                  <input
                    type="checkbox"
                    id="autoExtraction"
                    checked={formData.autoExtraction ?? true}
                    onChange={(e) => setFormData({ ...formData, autoExtraction: e.target.checked })}
                  />
                  <label htmlFor="autoExtraction">Habilitar Indexação Automática de Metadados</label>
                </SwitchRow>

                <SwitchRow>
                  <input
                    type="checkbox"
                    id="isDefault"
                    checked={formData.isDefault ?? false}
                    onChange={(e) => setFormData({ ...formData, isDefault: e.target.checked })}
                  />
                  <label htmlFor="isDefault">Tornar este o provedor padrão do sistema</label>
                </SwitchRow>
              </div>

              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 12, marginTop: 20 }}>
                <Button type="button" variant="secondary" onClick={() => setIsModalOpen(false)}>
                  Cancelar
                </Button>
                <Button type="submit">
                  <FaCheck /> Salvar Configuração
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}
    </Container>
  );
};

export default AiSettingsPage;
